/**
 * The browser's Back and Forward, held while a record has an unsaved draft.
 *
 * The App Router answers `popstate` with a navigation, from a listener it adds
 * when it mounts, and listeners on `window` run in the order they were added.
 * A record page mounts later, and a capture listener it added was not called
 * for `popstate` in Chromium while the router's listener was (observed with
 * e2e/opaas/admin-request-sections.spec.ts, "the browser's Back asks before an
 * unsaved draft is dropped"). So one dispatcher is added before the router
 * mounts (instrumentation-client.ts), and a mounted record hands it the guard
 * that decides. A traversal the guard holds reaches no later listener.
 */

/** Answers true when it held the traversal the event reports. */
export type TraversalGuard = (event: PopStateEvent) => boolean;

let current: TraversalGuard | null = null;
const installedOn = new WeakSet<EventTarget>();

/** Adds the dispatcher once per window; it must run before the App Router mounts. */
export function installHistoryGuard(target: EventTarget = window): void {
  if (installedOn.has(target)) return;
  installedOn.add(target);
  target.addEventListener("popstate", (event) => {
    if (current?.(event as PopStateEvent)) event.stopImmediatePropagation();
  });
}

/** Lets `guard` decide the traversals until the returned release runs. */
export function holdHistoryTraversals(guard: TraversalGuard): () => void {
  current = guard;
  return () => {
    if (current === guard) current = null;
  };
}
